import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useTenant } from '@/services/tenant-context';
import {
  accountsApi,
  collectionsApi,
  customersApi,
  invoicesApi,
  outstandingsApi,
  packagingApi,
  settlementsApi,
  visibilityApi,
} from '@/services/kosh-api';
import {
  DEFAULT_LABELS,
  VERTICAL_LABELS,
  VERTICAL_NAMES,
  pluralize,
} from '@/config/kosh-verticals';
import type {
  KoshInvoiceStatus,
  KoshLabelKey,
  KoshLabels,
  KoshVertical,
} from '@/types/kosh';

/** Tenant id for query-key scoping — every Kosh read is tenant-bound. */
function useScope(): string {
  const { currentTenant } = useTenant();
  return currentTenant.id;
}

// ───────────────────────── Packaging template / labels ─────────────────────
export function usePackagingProfiles() {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'packaging', 'profiles', scope],
    queryFn: packagingApi.profiles,
    staleTime: 5 * 60_000,
  });
}

export interface KoshLabelContext {
  vertical: KoshVertical | null;
  verticalName: string;
  labels: KoshLabels;
  /** Singular label for a key (e.g. "Order"). */
  label: (key: KoshLabelKey) => string;
  /** Plural label for a key (e.g. "Orders"). */
  labelPlural: (key: KoshLabelKey) => string;
  isLoading: boolean;
}

/**
 * Resolves the tenant's business-template terminology. Reads the default
 * packaging profile, layers its label overrides on the vertical preset, and
 * falls back to neutral defaults before it loads. This is what makes "one
 * capability, many packagings" visible in the UI.
 */
export function useKoshLabels(): KoshLabelContext {
  const profilesQ = usePackagingProfiles();

  return useMemo(() => {
    const profiles = profilesQ.data ?? [];
    const profile = profiles.find((p) => p.isDefault) ?? profiles[0] ?? null;
    const vertical = profile?.vertical ?? null;
    const base = vertical ? VERTICAL_LABELS[vertical] : DEFAULT_LABELS;
    const labels: KoshLabels = { ...base, ...(profile?.labels ?? {}) };
    return {
      vertical,
      verticalName: vertical ? VERTICAL_NAMES[vertical] : 'Default',
      labels,
      label: (k) => labels[k],
      labelPlural: (k) => pluralize(labels[k]),
      isLoading: profilesQ.isLoading,
    };
  }, [profilesQ.data, profilesQ.isLoading]);
}

// ───────────────────────── Commercial ──────────────────────────────────────
export function useAccounts() {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'accounts', scope],
    queryFn: () => accountsApi.list({ pageSize: 200 }),
  });
}
export function useAccount(id: string | undefined) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'accounts', scope, id],
    queryFn: () => accountsApi.get(id!),
    enabled: Boolean(id),
  });
}
export function useCoreCustomers() {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'core-customers', scope],
    // The /customers endpoint caps pageSize at 200 — 500 fails validation (400),
    // which empties the name map and shows every customer as a raw UUID.
    queryFn: () => customersApi.list({ pageSize: 200 }),
    staleTime: 5 * 60_000,
  });
}

/** Map of core customerId → display name, for enriching invoices/outstandings. */
export function useCustomerNameMap() {
  const q = useCoreCustomers();
  const map = useMemo(() => {
    const m = new Map<string, string>();
    (q.data ?? []).forEach((c) => m.set(c.id, c.name));
    return m;
  }, [q.data]);
  return map;
}

// ───────────────────────── Billing ─────────────────────────────────────────
export function useInvoices(params?: Record<string, string | number | undefined>) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'invoices', scope, params ?? {}],
    queryFn: () => invoicesApi.list({ pageSize: 200, ...params }),
  });
}
export function useInvoice(id: string | undefined) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'invoices', scope, id],
    queryFn: () => invoicesApi.get(id!),
    enabled: Boolean(id),
  });
}

// ───────────────────────── Receivables ─────────────────────────────────────
export function useOutstandings(params?: Record<string, string | number | undefined>) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'outstandings', scope, params ?? {}],
    queryFn: () => outstandingsApi.list({ pageSize: 200, ...params }),
  });
}
export function useOutstanding(id: string | undefined) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'outstandings', scope, id],
    queryFn: () => outstandingsApi.get(id!),
    enabled: Boolean(id),
  });
}

// ───────────────────────── Collections ─────────────────────────────────────
export function useCollectionActivities() {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'collection-activities', scope],
    queryFn: () => collectionsApi.activities({ pageSize: 200 }),
  });
}
export function useReminders() {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'reminders', scope],
    queryFn: () => collectionsApi.reminders({ pageSize: 200 }),
  });
}
export function useSettlements() {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'settlements', scope],
    queryFn: () => settlementsApi.list({ pageSize: 200 }),
  });
}

// ───────────────────────── Visibility ──────────────────────────────────────
export function useKoshDashboard(currency?: string) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'dashboard', scope, currency ?? 'all'],
    queryFn: () => visibilityApi.dashboard(currency ? { currency } : undefined),
  });
}

// ───────────────────────── Mutations ───────────────────────────────────────
function useKoshInvalidate() {
  const qc = useQueryClient();
  return (keys: QueryKey[]) => keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
}

export function useInvoiceTransition() {
  const invalidate = useKoshInvalidate();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: KoshInvoiceStatus }) =>
      invoicesApi.transition(id, to),
    onSuccess: () => invalidate([['kosh', 'invoices'], ['kosh', 'outstandings'], ['kosh', 'dashboard']]),
  });
}

export function useRecordSettlement() {
  const invalidate = useKoshInvalidate();
  return useMutation({
    mutationFn: (body: { outstandingId: string; amount: number; currency: string; method?: string; reference?: string }) =>
      settlementsApi.create(body),
    onSuccess: () =>
      invalidate([['kosh', 'outstandings'], ['kosh', 'settlements'], ['kosh', 'dashboard'], ['kosh', 'invoices']]),
  });
}

export function useSettlementConfirm() {
  const invalidate = useKoshInvalidate();
  return useMutation({
    mutationFn: (id: string) => settlementsApi.confirm(id),
    onSuccess: () =>
      invalidate([['kosh', 'outstandings'], ['kosh', 'settlements'], ['kosh', 'dashboard'], ['kosh', 'invoices']]),
  });
}

export function useOutstandingDispute() {
  const invalidate = useKoshInvalidate();
  return useMutation({
    mutationFn: ({ id, inDispute }: { id: string; inDispute: boolean }) =>
      outstandingsApi.dispute(id, inDispute),
    onSuccess: () => invalidate([['kosh', 'outstandings'], ['kosh', 'dashboard']]),
  });
}

export function useCollectionActivityComplete() {
  const invalidate = useKoshInvalidate();
  return useMutation({
    mutationFn: (id: string) => collectionsApi.completeActivity(id),
    onSuccess: () => invalidate([['kosh', 'collection-activities']]),
  });
}
